/**
 * The composer: words, up to five files (picker, paste or drop), a reply bar, and the + menu with
 * Photo or video, Record a video, Take a photo (on a phone) and File.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";
import { fileType } from "./Bubbles";
import { type Chip, usePage } from "./context";
import { IcCamera, IcDoc, IcImage, IcPlay, IcPlus, IcReply, IcUp, IcVideo, IcX } from "./icons";
import { snippet } from "./model";

/** At most this many files go with one message. */
export const ATTACH_MAX = 5;
/** Pictures and documents go through the page's own upload, up to its 20 MB. */
const FILE_MAX = 20 * 1024 * 1024;
const GROW_MAX = 140;

const DOC_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  rtf: "application/rtf",
  odt: "application/vnd.oasis.opendocument.text",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ods: "application/vnd.oasis.opendocument.spreadsheet",
  csv: "text/csv",
  txt: "text/plain",
  md: "text/markdown",
};

const fmtSize = (b: number) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1e3))} KB`);

/** What a file is to the chat, or null when it cannot be sent: a picture, a video, or a document. */
function chipOf(f: File, id: string): Chip | null {
  const name = f.name || "Pasted image.png";
  const ext = name.toLowerCase().split(".").pop() ?? "";
  const t = f.type || "";
  if (t.startsWith("image/")) return { id, file: f, name, size: fmtSize(f.size), kind: "image", ext: "img", label: "Image", preview: null };
  if (t.startsWith("video/") || /^(mp4|mov|m4v|webm)$/.test(ext)) return { id, file: f, name, size: fmtSize(f.size), kind: "video", ext: "vid", label: "Video", preview: null };
  const type = DOC_TYPES[ext];
  if (!type) return null;
  // Why: a browser can hand a document over with no type; the upload checks the type, so it gets the one its name says.
  const file = t ? f : new File([f], name, { type });
  return { id, file, name, size: fmtSize(f.size), kind: "file", ext: fileType(name).ext, label: fileType(name).label, preview: null };
}

function addChips(have: Chip[], files: readonly File[], uid: () => string): { chips: Chip[]; note: string } {
  let skipped = 0;
  let bad = 0;
  let big = 0;
  const chips = [...have];
  for (const f of files) {
    const c = chipOf(f, uid());
    if (!c) {
      bad++;
      continue;
    }
    if (c.kind !== "video" && f.size > FILE_MAX) {
      big++;
      continue;
    }
    if (chips.length >= ATTACH_MAX) {
      skipped++;
      continue;
    }
    chips.push(c);
  }
  const note = skipped
    ? `Up to ${ATTACH_MAX} files at a time. ${skipped} ${skipped === 1 ? "wasn’t" : "weren’t"} added.`
    : big
      ? "Pictures and files can be 20 MB at most."
      : bad
        ? "Only photos, videos, PDF, Word, Excel and text files can be sent."
        : "";
  return { chips, note };
}

let uidN = 0;
const uid = () => `a${++uidN}`;

export function Composer({ dropped, onDropped }: { dropped: File[] | null; onDropped: () => void }): ReactNode {
  const { host, phone, replying, setReplying, convo, sendText, openMenu, menu, openSheet, noteTyping, defaultRoom } = usePage();
  const [text, setText] = useState("");
  const [chips, setChips] = useState<Chip[]>([]);
  const [note, setNote] = useState("");
  const ta = useRef<HTMLTextAreaElement>(null);
  const fileIn = useRef<HTMLInputElement>(null);
  const camIn = useRef<HTMLInputElement>(null);

  const grow = () => {
    const el = ta.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, GROW_MAX)}px`;
  };
  const add = (files: readonly File[]) => {
    const r = addChips(chips, files, uid);
    setChips(r.chips.map((c) => (c.kind === "image" && !c.preview ? { ...c, preview: URL.createObjectURL(c.file) } : c)));
    setNote(r.note);
    ta.current?.focus();
  };
  const addRef = useRef(add);
  addRef.current = add;
  useEffect(() => {
    if (!dropped) return;
    addRef.current(dropped);
    onDropped();
  }, [dropped, onDropped]);
  useEffect(() => {
    if (replying) ta.current?.focus();
  }, [replying]);

  const send = () => {
    const body = text.trim();
    if (!body && chips.length === 0) return;
    sendText(body, chips);
    setText("");
    setChips([]);
    setNote("");
    window.requestAnimationFrame(grow);
  };

  const on = Boolean(text.trim() || chips.length);
  const target = replying && "entryKey" in replying ? convo.byId.get(replying.entryKey) : undefined;
  const pick = (accept: string) => {
    const el = fileIn.current;
    if (!el) return;
    el.accept = accept;
    el.click();
  };
  const plus = (anchor: HTMLElement) => {
    if (menu?.attach) {
      openMenu(null);
      return;
    }
    openMenu({
      anchor: anchor.getBoundingClientRect(),
      attach: true,
      items: [
        { key: "photo", icon: <IcImage s={18} />, label: "Photo or video", run: () => pick("image/*,video/*") },
        { key: "rec", icon: <IcVideo s={18} />, label: "Record a video", run: () => openSheet("rec") },
        ...(phone ? [{ key: "cam", icon: <IcCamera s={18} />, label: "Take a photo", run: () => camIn.current?.click() }] : []),
        {
          key: "file",
          icon: <IcDoc s={18} />,
          label: "File",
          sub: "PDF, Word, Excel, text",
          run: () => pick(".pdf,.doc,.docx,.rtf,.odt,.xls,.xlsx,.csv,.ods,.txt,.md,application/pdf,text/plain"),
        },
      ],
    });
  };

  return (
    <div className="composer">
      <button
        type="button"
        className={`cplus${menu?.attach ? " open" : ""}`}
        aria-label="Add a photo, video or file"
        data-menu-anchor
        onClick={(e) => plus(e.currentTarget)}
      >
        <IcPlus s={18} />
      </button>
      <div className="field">
        {replying ? (
          <div className="replying">
            <IcReply s={16} />
            <div className="rq">
              <b>{replying.kind === "announcement" ? `Replying to ${host.first}’s announcement` : `Replying to ${target?.from === "me" ? "yourself" : host.first}`}</b>
              <span>{replying.kind === "announcement" ? replying.post.body : snippet(target)}</span>
            </div>
            <button type="button" className="xs" aria-label="Cancel reply" onClick={() => setReplying(null)}>
              <IcX s={14} />
            </button>
          </div>
        ) : null}
        {chips.length || note ? (
          <div className="attp">
            {chips.map((c) => (
              <span className="achip" key={c.id}>
                <span className={`at${c.kind === "file" ? ` ftype t-${c.ext}` : ""}`}>
                  {c.kind === "image" && c.preview ? <img src={c.preview} alt="" /> : c.kind === "video" ? <IcPlay s={14} /> : c.kind === "image" ? <IcImage s={16} /> : fileType(c.name).badge}
                </span>
                <span className="an">
                  <b>{c.name}</b>
                  <small>{c.size}</small>
                </span>
                <button
                  type="button"
                  className="xs"
                  aria-label={`Remove ${c.name}`}
                  onClick={() => {
                    if (c.preview) URL.revokeObjectURL(c.preview);
                    setChips((have) => have.filter((x) => x.id !== c.id));
                    setNote("");
                  }}
                >
                  <IcX s={12} />
                </button>
              </span>
            ))}
            {note ? <span className="att-note">{note}</span> : chips.length ? <span className="att-note quiet">{chips.length} of {ATTACH_MAX}</span> : null}
          </div>
        ) : null}
        <div className="inrow">
          <label className="sr" htmlFor="tp-ta">
            Message <bdi>{host.first}</bdi>
          </label>
          <textarea
            id="tp-ta"
            ref={ta}
            dir="auto"
            rows={1}
            placeholder={`Message ${host.first}`}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              grow();
              noteTyping(replying?.webinar_id ?? defaultRoom);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                send();
              }
              if (e.key === "Escape" && replying) setReplying(null);
            }}
            onPaste={(e) => {
              const fs = e.clipboardData?.files;
              if (fs?.length) {
                e.preventDefault();
                add([...fs]);
              }
            }}
          />
          <button type="button" className={`send${on ? " on" : ""}`} aria-label="Send" onClick={send}>
            <IcUp s={17} />
          </button>
        </div>
      </div>
      <input
        ref={fileIn}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) add([...e.target.files]);
          e.target.value = "";
        }}
      />
      <input
        ref={camIn}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          if (e.target.files) add([...e.target.files]);
          e.target.value = "";
        }}
      />
    </div>
  );
}
